"use client";

import { useState, useCallback, useEffect, useId } from "react";
import { motion } from "motion/react";
import {
  Sparkles,
  Users,
  MapPin,
  Building2,
  Calendar,
  Banknote,
  Phone,
} from "lucide-react";
import { getPropertyAiSummary } from "@/server-actions/ai/generate-summary";
import type { PropertySummaryOutput } from "@/lib/ai/schemas";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@/components/ui/drawer";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/types/database";

interface PropertyAiSummaryProps {
  propertyId: number;
  propertyName: string;
  estimatedCompletion: string | null;
  priceFrom: number;
  currency: string;
  phoneNumber: string;
}

type SummaryState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; data: PropertySummaryOutput }
  | { status: "error"; message: string };

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

function SummarySkeleton() {
  return (
    <div className="space-y-5 p-6 md:p-8 animate-pulse">
      <div className="space-y-1.5">
        <div className="h-5 w-full bg-muted rounded" />
        <div className="h-5 w-2/3 bg-muted rounded" />
      </div>
      <div className="border-t border-border" />
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="h-4 w-4 bg-muted rounded shrink-0 mt-0.5" />
          <div className="space-y-1.5 flex-1">
            <div className="h-3 w-16 bg-muted rounded" />
            <div className="h-3 w-full bg-muted rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

function WhatsAppIcon({
  className,
  title,
}: {
  className?: string;
  title?: string;
}) {
  const titleId = useId();
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      {...(title
        ? { role: "img" as const, "aria-labelledby": titleId }
        : { "aria-hidden": true as const })}
    >
      {title && <title id={titleId}>{title}</title>}
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

/** Strips everything except digits and a leading '+' → safe for tel: href */
function normalizeTelNumber(phone: string): string {
  const stripped = phone.replace(/[^\d+]/g, "");
  // Ensure '+' only appears at position 0
  return stripped.charAt(0) === "+"
    ? "+" + stripped.slice(1).replace(/\+/g, "")
    : stripped.replace(/\+/g, "");
}

/** Strips everything except digits → international format for wa.me/ */
function normalizeWhatsAppNumber(phone: string): string {
  return phone.replace(/\D/g, "");
}

/** Renders text with **bold** markdown markers as <strong> tags */
function RichText({ text, className }: { text: string; className?: string }) {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  const occurrences = new Map<string, number>();
  return (
    <p className={className}>
      {parts.map((part, i) => {
        const count = occurrences.get(part) ?? 0;
        occurrences.set(part, count + 1);
        const isBold = i % 2 === 1;
        const key = `${isBold ? "bold" : "text"}:${part}#${count}`;
        return isBold ? (
          <strong key={key} className="text-foreground font-semibold">
            {part}
          </strong>
        ) : (
          <span key={key}>{part}</span>
        );
      })}
    </p>
  );
}

function SummaryContent({
  data,
  estimatedCompletion,
  priceFrom,
  currency,
  phoneNumber,
}: {
  data: PropertySummaryOutput;
  estimatedCompletion: string | null;
  priceFrom: number;
  currency: string;
  phoneNumber: string;
}) {
  const telNumber = normalizeTelNumber(phoneNumber);
  const whatsappNumber = normalizeWhatsAppNumber(phoneNumber);

  const rows = [
    {
      icon: <Users className="h-4 w-4 text-primary/60 stroke-[1] shrink-0 mt-0.5" />,
      label: "Best For",
      value: data.best_for,
      rich: true,
    },
    {
      icon: <MapPin className="h-4 w-4 text-primary/60 stroke-[1] shrink-0 mt-0.5" />,
      label: "Location",
      value: data.location,
      rich: true,
    },
    {
      icon: <Building2 className="h-4 w-4 text-primary/60 stroke-[1] shrink-0 mt-0.5" />,
      label: "Community",
      value: data.community,
      rich: true,
    },
    {
      icon: <Calendar className="h-4 w-4 text-primary/60 stroke-[1] shrink-0 mt-0.5" />,
      label: "Estimated Possession",
      value: `**${estimatedCompletion ?? "Ready to move in"}**`,
      rich: true,
    },
    {
      icon: <Banknote className="h-4 w-4 text-primary/60 stroke-[1] shrink-0 mt-0.5" />,
      label: "Price",
      value: `**${formatPrice(priceFrom, currency)}**`,
      rich: true,
    },
  ];

  return (
    <div className="p-6 md:p-8 space-y-5">
      {/* Headline — no label */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0 }}
        className="font-serif text-lg md:text-xl text-primary leading-relaxed"
      >
        {data.headline}
      </motion.p>

      <div className="border-t border-border" />

      {/* Data rows with staggered animation */}
      {rows.map((row, index) => (
        <motion.div
          key={row.label}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: (index + 1) * 0.08 }}
          className="flex items-start gap-3"
        >
          {row.icon}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-secondary mb-0.5">
              {row.label}
            </p>
            {row.rich ? (
              <RichText text={row.value} className="text-sm text-muted-foreground" />
            ) : (
              <p className="text-sm text-muted-foreground">{row.value}</p>
            )}
          </div>
        </motion.div>
      ))}

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.56 }}
        className="border-t border-border pt-4"
      >
        <p className="font-serif text-lg md:text-xl text-primary text-center mb-3">
          Have questions? We&apos;re here to help
        </p>
        <div className="flex items-center justify-center gap-4">
          <a
            href={`tel:${telNumber}`}
            className={cn(
              "inline-flex items-center gap-2 rounded-xl px-6 h-10",
              "uppercase tracking-widest text-xs font-bold",
              "border border-primary text-primary",
              "hover:bg-primary hover:text-white transition-all duration-300"
            )}
          >
            <Phone className="h-3.5 w-3.5" />
            Call Us
          </a>
          <a
            href={`https://wa.me/${whatsappNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "inline-flex items-center gap-2 rounded-xl px-6 h-10",
              "uppercase tracking-widest text-xs font-bold",
              "border border-green-700 text-green-700",
              "hover:bg-green-700 hover:text-white transition-all duration-300"
            )}
          >
            <WhatsAppIcon className="h-3.5 w-3.5" title="WhatsApp" />
            WhatsApp
          </a>
        </div>
      </motion.div>
    </div>
  );
}

function SummaryFooter() {
  return (
    <p className="text-xs text-muted-foreground text-center px-6 pb-4">
      AI-generated summary based on property data
    </p>
  );
}

export function PropertyAiSummary({
  propertyId,
  propertyName,
  estimatedCompletion,
  priceFrom,
  currency,
  phoneNumber,
}: PropertyAiSummaryProps) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<SummaryState>({ status: "idle" });
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const fetchSummary = useCallback(async () => {
    if (state.status === "loaded" || state.status === "loading") return;
    setState({ status: "loading" });
    try {
      const result = await getPropertyAiSummary(propertyId);
      if (result.success) {
        setState({ status: "loaded", data: result.data });
      } else {
        setState({ status: "error", message: result.error });
      }
    } catch (err) {
      console.error("[AI Summary] fetch failed:", err);
      setState({
        status: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [propertyId, state.status]);

  const handleOpen = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      fetchSummary();
    }
  };

  const panelBody =
    state.status === "loading" ? (
      <SummarySkeleton />
    ) : state.status === "loaded" ? (
      <SummaryContent
        data={state.data}
        estimatedCompletion={estimatedCompletion}
        priceFrom={priceFrom}
        currency={currency}
        phoneNumber={phoneNumber}
      />
    ) : state.status === "error" ? (
      <div className="p-6 text-center text-sm text-muted-foreground">
        {state.message}
      </div>
    ) : null;

  const floatingCta = (
    <button
      type="button"
      onClick={() => handleOpen(true)}
      className={cn(
        "fixed right-4 bottom-20 md:bottom-auto md:right-6 md:top-1/2 md:-translate-y-1/2 z-40",
        "inline-flex items-center gap-2 rounded-xl px-5 h-11",
        "bg-primary text-white",
        "text-xs font-bold uppercase tracking-widest",
        "shadow-lg hover:shadow-xl",
        "transition-all duration-300 cursor-pointer",
        open && "opacity-0 pointer-events-none"
      )}
    >
      <Sparkles className="h-4 w-4" />
      AI Summary
    </button>
  );

  if (isDesktop) {
    return (
      <>
        {floatingCta}
        <Sheet open={open} onOpenChange={handleOpen}>
          <SheetContent side="right" className="sm:max-w-lg w-full flex flex-col !inset-y-auto !top-4 !bottom-4 !right-4 !h-auto !max-h-[calc(100vh-2rem)] rounded-2xl shadow-2xl">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 text-base font-serif">
                <Sparkles className="h-4 w-4 text-primary" />
                Quick Summary
              </SheetTitle>
              <SheetDescription className="text-sm font-serif text-muted-foreground">
                {propertyName}
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto">{panelBody}</div>
            <SheetFooter>
              <SummaryFooter />
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <>
      {floatingCta}
      <Drawer open={open} onOpenChange={handleOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2 text-base font-serif justify-center">
              <Sparkles className="h-4 w-4 text-primary" />
              Quick Summary
            </DrawerTitle>
            <DrawerDescription className="text-sm font-serif text-muted-foreground text-center">
              {propertyName}
            </DrawerDescription>
          </DrawerHeader>
          <div className="max-h-[60vh] overflow-y-auto">{panelBody}</div>
          <DrawerFooter>
            <SummaryFooter />
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
